import { isDeckId, isWorkbookId } from 'pc-types';
import type { TourState, TourStatePatch } from 'pc-types';

export const TOUR_STAGES = [
  'fork',
  'demoSpace',
  'dataRoom',
  'openModel',
  'sheetPrompt',
  'sheetReplay',
  'sheetDone',
  'backToSpace',
  'openCim',
  'deckPrompt',
  'deckReplay',
  'deckDone',
  'commentsIntro',
  'commentsPrompt',
  'commentsReplay',
  'voiceIntro',
  'chromeShare',
  'graduation',
] as const;
export type TourStage = (typeof TOUR_STAGES)[number];

export function isTourStage(value: unknown): value is TourStage {
  return TOUR_STAGES.includes(value as TourStage);
}

export function tourStageIndex(stage: TourStage): number {
  return TOUR_STAGES.indexOf(stage);
}

/** Signals the app emits that can advance the tour machine. */
export type TourSignal =
  | { type: 'next' }
  | { type: 'target-clicked' }
  | { type: 'editor-opened'; kind: 'sheet' | 'deck' }
  | { type: 'space-files-opened' }
  | {
      type: 'message-sent';
      documentId: string;
      demoReplayId?: 'sheet' | 'deck' | 'comments';
    }
  | { type: 'run-completed'; documentId: string }
  | { type: 'run-failed'; documentId: string }
  /**
   * Mount-time reconcile against GET /spaces/demo/status: a reload during or
   * after a replay loses the live run-completed event, so the server's
   * completed flag decides whether the replay stage advances or rewinds to
   * its prompt stage for a resend.
   */
  | { type: 'replay-reconciled'; outcome: 'completed' | 'not-run' }
  | { type: 'voice-tried' };

/**
 * The pure stage-transition function: returns the next stage, or null when
 * the signal does not advance the current stage.
 */
export function transitionTourStage(
  stage: TourStage,
  signal: TourSignal
): TourStage | null {
  switch (stage) {
    case 'demoSpace':
      return signal.type === 'target-clicked' ? 'dataRoom' : null;
    case 'dataRoom':
      return signal.type === 'next' ? 'openModel' : null;
    case 'openModel':
      return signal.type === 'editor-opened' && signal.kind === 'sheet'
        ? 'sheetPrompt'
        : null;
    case 'sheetPrompt':
      return signal.type === 'message-sent' && signal.demoReplayId === 'sheet'
        ? 'sheetReplay'
        : null;
    case 'sheetReplay':
      if (signal.type === 'run-completed' && isWorkbookId(signal.documentId)) {
        return 'sheetDone';
      }
      if (signal.type === 'run-failed' && isWorkbookId(signal.documentId)) {
        return 'sheetPrompt';
      }
      if (signal.type === 'replay-reconciled') {
        return signal.outcome === 'completed' ? 'sheetDone' : 'sheetPrompt';
      }
      return null;
    case 'sheetDone':
      return signal.type === 'next' ? 'backToSpace' : null;
    case 'backToSpace':
      return signal.type === 'space-files-opened' ? 'openCim' : null;
    case 'openCim':
      return signal.type === 'editor-opened' && signal.kind === 'deck'
        ? 'deckPrompt'
        : null;
    case 'deckPrompt':
      return signal.type === 'message-sent' && signal.demoReplayId === 'deck'
        ? 'deckReplay'
        : null;
    case 'deckReplay':
      if (signal.type === 'run-completed' && isDeckId(signal.documentId)) {
        return 'deckDone';
      }
      if (signal.type === 'run-failed' && isDeckId(signal.documentId)) {
        return 'deckPrompt';
      }
      if (signal.type === 'replay-reconciled') {
        return signal.outcome === 'completed' ? 'deckDone' : 'deckPrompt';
      }
      return null;
    case 'deckDone':
      return signal.type === 'next' ? 'commentsIntro' : null;
    case 'commentsIntro':
      return signal.type === 'next' ? 'commentsPrompt' : null;
    case 'commentsPrompt':
      return signal.type === 'message-sent' &&
        signal.demoReplayId === 'comments'
        ? 'commentsReplay'
        : null;
    case 'commentsReplay':
      if (signal.type === 'run-completed' && isDeckId(signal.documentId)) {
        return 'voiceIntro';
      }
      if (signal.type === 'run-failed' && isDeckId(signal.documentId)) {
        return 'commentsPrompt';
      }
      if (signal.type === 'replay-reconciled') {
        return signal.outcome === 'completed' ? 'voiceIntro' : 'commentsPrompt';
      }
      return null;
    case 'voiceIntro':
      return signal.type === 'next' || signal.type === 'voice-tried'
        ? 'chromeShare'
        : null;
    case 'chromeShare':
      return signal.type === 'next' ? 'graduation' : null;
    // 'fork' advances from the welcome page, 'graduation' from its dialog.
    case 'fork':
    case 'graduation':
      return null;
  }
}

/**
 * Checklist items completed by reaching a stage. 'space' is not derived —
 * it completes when the user actually creates their own space.
 */
export function completedStepsForStage(stage: TourStage): string[] {
  const i = tourStageIndex(stage);
  const done: string[] = [];
  if (i >= tourStageIndex('dataRoom')) done.push('explore');
  if (i >= tourStageIndex('sheetDone')) done.push('sheet');
  if (i >= tourStageIndex('deckDone')) done.push('deck');
  if (i >= tourStageIndex('voiceIntro')) done.push('comments');
  return done;
}

export function mergeCompletedSteps(
  current: readonly string[],
  additions: readonly string[]
): string[] {
  const merged = new Set(current);
  for (const step of additions) merged.add(step);
  return [...merged];
}

const FALLBACK_TOUR_STATE: TourState = {
  status: 'not_started',
  stage: 'fork',
  completedSteps: [],
};

export function applyOptimisticTourStatePatch(
  current: TourState | undefined,
  patch: TourStatePatch
): TourState {
  const baseline = current ?? FALLBACK_TOUR_STATE;
  return {
    status: patch.status ?? baseline.status,
    stage: patch.stage ?? baseline.stage,
    completedSteps: patch.completedSteps ?? baseline.completedSteps,
    demoSpaceId: patch.demoSpaceId ?? baseline.demoSpaceId,
  };
}
